import { useTranslation } from "react-i18next";
import {
  CheckCircle,
  Money,
  Star,
  TrendDown,
  TrendUp,
  Wallet,
  type Icon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

function Trend({ change, onHero }: { change: number | null | undefined; onHero?: boolean }) {
  if (change == null) return null;
  const positive = change >= 0;
  const TrendIcon = positive ? TrendUp : TrendDown;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
        onHero
          ? "bg-white/15 text-white"
          : positive
            ? "bg-emerald-50 text-emerald-700"
            : "bg-rose-50 text-rose-700",
      )}
    >
      <TrendIcon className="size-3" />
      {positive ? "+" : ""}
      {change.toFixed(1)}%
    </span>
  );
}

function Kpi({
  icon: Icon,
  label,
  value,
  change,
  foot,
  hero,
}: {
  icon: Icon;
  label: string;
  value: string;
  change?: number | null;
  foot?: string;
  hero?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-2.5 overflow-hidden rounded-2xl border p-5 shadow-sm",
        hero
          ? "col-span-2 border-transparent text-white md:col-span-3 lg:col-span-1"
          : "border-border/50 bg-card",
      )}
      style={
        hero
          ? { background: "linear-gradient(150deg, oklch(37.12% 0.059 186.62), oklch(31.52% 0.0535 186.62))" }
          : undefined
      }
    >
      {hero && (
        <span
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-8 size-32 rounded-full"
          style={{ background: "oklch(98% 0 0 / 0.06)" }}
        />
      )}
      <span
        className={cn(
          "inline-flex items-center gap-2 text-xs font-medium",
          hero ? "text-white/75" : "text-muted-foreground",
        )}
      >
        <Icon className="size-4" weight="duotone" />
        {label}
      </span>
      <div className="flex flex-wrap items-baseline gap-2">
        <p
          className={cn("font-bold leading-none tabular-nums", hero ? "text-[28px]" : "text-2xl text-foreground")}
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {value}
        </p>
        <Trend change={change} onHero={hero} />
      </div>
      {foot && (
        <p className={cn("text-xs", hero ? "text-white/70" : "text-muted-foreground")}>{foot}</p>
      )}
    </div>
  );
}

export function TherapistKpiBand({
  earnings,
  earningsChange,
  completedSessions,
  completedChange,
  pendingPayout,
  pendingPayoutSessionCount,
  avgRating,
  reviewCount,
  formatCurrency,
}: {
  earnings: number;
  earningsChange: number | null;
  completedSessions: number;
  completedChange: number | null;
  pendingPayout: number;
  pendingPayoutSessionCount: number;
  avgRating: number | null;
  reviewCount: number;
  formatCurrency: (value: number) => string;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 lg:grid-cols-[1.5fr_repeat(3,1fr)]">
      <Kpi
        hero
        icon={Money}
        label={t("therapist.dashboard.kpi.earningsInPeriod")}
        value={formatCurrency(earnings)}
        change={earningsChange}
      />
      <Kpi
        icon={CheckCircle}
        label={t("therapist.dashboard.kpi.completedSessions")}
        value={String(completedSessions)}
        change={completedChange}
      />
      <Kpi
        icon={Wallet}
        label={t("therapist.dashboard.kpi.pendingPayout")}
        value={formatCurrency(pendingPayout)}
        foot={t("therapist.dashboard.kpi.pendingPayoutSessions", { count: pendingPayoutSessionCount })}
      />
      <Kpi
        icon={Star}
        label={t("therapist.dashboard.kpi.rating")}
        value={avgRating != null ? String(avgRating) : "—"}
        foot={
          reviewCount > 0
            ? t("therapist.dashboard.reviewsCount", { count: reviewCount })
            : t("therapist.dashboard.noReviews")
        }
      />
    </div>
  );
}
